import React, { useContext, useEffect, useMemo, useState } from "react";
import { CloseButton, Col, Container, Navbar, Offcanvas } from "react-bootstrap";
import SidebarHeader from "@me-team/host/src/components/Sidebar/SidebarHeader";
import SidebarSubheader from "@me-team/host/src/components/Sidebar/SidebarSubheader";
import LanguageDropdown from "@me-team/host/src/components/LanguageDropdown/LanguageDropdown";
import { Link, useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import SidebarMenu from "@me-team/host/src/components/Sidebar/SidebarMenu";
import { NavbarUserData } from "@me-team/host/src/components/Sidebar/SidebarLoggedUser";
import PaymentExpired from "../PaymentExpired/PaymentExpired";
import { WithRoleAccess } from "@services/ui-components";
import { BranchProxy, CompanyAccess, ModuleProxy } from "@me-team/host/main/graphql/types";
import { useTranslation } from "react-i18next";
import {
    useGetActiveCompaniesQuery,
    useGetCompanyModulesForCreateOwnerQuery,
    useGetCompanyModulesQuery,
} from "@me-team/host/main/company/graphql/company.hooks";
import AccessService from "@me-team/host/services/AccessService";
import { useGetBranchesQuery } from "@me-team/host/main/branches/graphql/branches.hooks";
import { branchesLength, isRefetchBranches } from "@me-team/host/main/branches/state";
import { refetchUserDataMenu } from "@me-team/host/src/apollo/apollo-client";
import { useReactiveVar } from "@apollo/client";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
    currentBranchEmployeeIdVar,
    currentBranchIdVar,
    currentCompanyIdVar,
    userIdVar,
} from "@me-team/host/main/globalVar/state";

import { useUpdateUserMutation } from "@me-team/host/main/globalVar/graphql/globalVar.hooks";
import SidebarService from "../../services/SidebarService/SidebarService";
import SidebarBranchList from "@me-team/host/src/components/Sidebar/SidebarBranchList";
import SidebarLoggedUser from "@me-team/host/src/components/Sidebar/SidebarLoggedUser";
import ModalUserActiveCompanies from "../MainMenu/ModalUserActiveCompanies";
import ModalChangeUserPassword from "../ChangeUserPassword/ModalChangeUserPassword";
import { useToast } from "../../hooks/useToast";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import { AccessContext } from "@me-team/host/src/components/App/App";

interface NavbarWrapperProps {
    userData?: NavbarUserData | null;
}

const NavbarWrapper: React.FC<NavbarWrapperProps> = ({ userData }) => {
    const { t: translate } = useTranslation();
    useContext(AccessContext);
    const { branchId: branchIdFromParams } = useParams();
    const isCreateOwnerPage = Boolean(useMatch("/create-owner/*"));
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const branchId: string = branchIdFromParams ?? String(currentBranchId);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const accessServiceInstance = AccessService.getInstance();
    const [isShowNavbar, setShowNavbar] = useState<boolean>(false);
    const navigate = useNavigate();
    const isRefetch = useReactiveVar(isRefetchBranches);
    const accessService = AccessService.getInstance().isPremium;
    const userId = useReactiveVar(userIdVar);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [showCompaniesModal, setShowCompaniesModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const handleCloseNavbar = () => setShowNavbar(false);
    const handleShowNavbar = () => setShowNavbar(true);

    const location = useLocation();
    useScrollToTop(location?.pathname);

    const isRegistrationOrCreateOwner = useMemo(
        () => location.pathname === "/registration" || location.pathname.startsWith("/create-owner"),
        [location.pathname]
    );

    const { data: activeCompaniesData, loading: activeCompaniesLoading } = useGetActiveCompaniesQuery({
        skip: !currentCompanyId || isRegistrationOrCreateOwner,
        variables: {
            companyId: +currentCompanyId,
        },
        context: {
            errorType: "global",
        },
    });

    const {
        data,
        loading,
        refetch: refetchBranches,
    } = useGetBranchesQuery({
        skip: !currentCompanyId || isRegistrationOrCreateOwner,
        variables: {
            itemsPerPage: 30,
            page: 1,
            branchTypeId: null,
            name: null,
            companyId: +currentCompanyId,
            employeeId: +currentBranchEmployeeId,
            onlyActive: true,
        },
        context: {
            errorType: "global",
        },
        onCompleted: (data) => {
            const filteredBranches: BranchProxy[] | [] = data?.user?.company?.branchesPaginated?.branches?.filter(
                (branch) => !branch.deleted
            );
            branchesLength(filteredBranches?.length);
        },
        onError(error: Error) {
            console.error(error);
        },
    });

    const {
        data: companyModulesData,
        loading: modulesLoading,
        refetch: refetchCompanyModules,
    } = useGetCompanyModulesQuery({
        skip: !currentCompanyId || isCreateOwnerPage,
        variables: { companyId: +currentCompanyId },
        context: {
            errorType: "global",
        },
    });

    const {
        data: companyModulesForCreateOwnerData,
        loading: companyModulesForCreateOwnerLoading,
        refetch: refetchCompanyModulesForCreateOwner,
    } = useGetCompanyModulesForCreateOwnerQuery({
        skip: !currentCompanyId || !isCreateOwnerPage,
        variables: { companyId: +currentCompanyId },
        context: {
            errorType: "global",
        },
    });

    const modulesData = isCreateOwnerPage ? companyModulesForCreateOwnerData : companyModulesData;
    const loadingModules = isCreateOwnerPage ? companyModulesForCreateOwnerLoading : modulesLoading;
    const refetchModules = isCreateOwnerPage ? refetchCompanyModulesForCreateOwner : refetchCompanyModules;

    const modulesList: ModuleProxy[] = modulesData?.user?.company?.modules || [];
    const sidebarService = new SidebarService(modulesList);

    useEffect(() => {
        if (currentCompanyId && currentBranchEmployeeId) {
            refetchBranches();
            refetchModules();
            refetchUserDataMenu();
            isRefetchBranches(false);
        }
    }, [isRefetch, currentCompanyId, currentBranchEmployeeId, refetchBranches, refetchModules]);

    const branches: BranchProxy[] | [] = useMemo(() => data?.user?.company?.branchesPaginated?.branches || [], [data]);

    const activeBranches: BranchProxy[] = useMemo(() => {
        const filtered = branches.filter((branch: BranchProxy) => !branch.deleted);
        branchesLength(filtered.length);
        return filtered;
    }, [branches]);

    const mainCompany = useMemo(() => activeCompaniesData?.user?.company, [activeCompaniesData?.user?.company]);

    const hasAbilityToCreateBranch = useMemo(
        () => accessService || activeBranches.length < 2,
        [accessService, activeBranches]
    );

    const [updateUser] = useUpdateUserMutation();
    const handleChangeBranch = async (branchId: number) => {
        if (branchId === currentBranchId) {
            navigate(`/branches/main/branch/${branchId}`);
            return;
        }
        try {
            userId &&
                currentBranchEmployeeId &&
                (await updateUser({
                    variables: {
                        companyId: mainCompany.id,
                        userId: Number(userId),
                        input: {
                            currentBranch: branchId,
                        },
                        branchId: Number(branchId),
                        employeeId: currentBranchEmployeeId,
                    },
                }));

            accessServiceInstance.beginRoleAccessReload(branchId);
            currentBranchIdVar(branchId);
            navigate(`/branches/main/branch/${branchId}`);
            await refetchUserDataMenu();
        } catch (error) {
            console.error(error);
        }
    };

    if (loading || loadingModules || activeCompaniesLoading) return <></>;

    return (
        <>
            {userId && (
                <Col xs={12} md={4} lg={3} xl={2} className="px-3">
                    {showCompaniesModal && (
                        <ModalUserActiveCompanies
                            show={showCompaniesModal}
                            close={() => setShowCompaniesModal(false)}
                            isButtonModalClose
                        />
                    )}
                    {showChangePasswordModal && (
                        <ModalChangeUserPassword
                            show={showChangePasswordModal}
                            close={() => setShowChangePasswordModal(false)}
                            toggleShowToast={toggleShowToast}
                        />
                    )}
                    <div className="d-md-none">
                        <Navbar fixed="top" expand={false} className="bg-white position-fixed border-bottom">
                            <Container fluid className="flex-nowrap">
                                {isRegistrationOrCreateOwner ? (
                                    <>
                                        <Navbar.Toggle className="border-0 px-1" onClick={handleShowNavbar}>
                                            <i className="bi bi-list fs-3"></i>
                                        </Navbar.Toggle>
                                        <div className="nav-item d-flex align-items-center flex-grow-1 justify-content-center text-truncate py-0 overflow-visible">
                                            <SidebarHeader isMobile branchId={branchId} />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Navbar.Toggle className="border-0 px-1" onClick={handleShowNavbar}>
                                            <i className="bi bi-list fs-3"></i>
                                        </Navbar.Toggle>
                                        <div className="nav-item d-flex align-items-center flex-grow-1 justify-content-center text-truncate py-0">
                                            <SidebarSubheader company={mainCompany} isMobile branchId={branchId} />
                                        </div>
                                        <LanguageDropdown isMobile branchId={branchId} />
                                    </>
                                )}

                                <Offcanvas show={isShowNavbar} onHide={handleCloseNavbar}>
                                    <Offcanvas.Header onHide className="border-bottom py-2">
                                        <CloseButton aria-label="Hide" className="m-0" onClick={handleCloseNavbar} />
                                        <Offcanvas.Title className="d-flex align-items-center flex-grow-1 justify-content-center text-truncate overflow-visible">
                                            {isRegistrationOrCreateOwner ? (
                                                <SidebarHeader isMobile branchId={branchId} />
                                            ) : (
                                                <SidebarSubheader
                                                    company={mainCompany}
                                                    handleCloseNavbar={handleCloseNavbar}
                                                    isMobile
                                                />
                                            )}
                                        </Offcanvas.Title>
                                        {!isRegistrationOrCreateOwner && (
                                            <LanguageDropdown
                                                handleCloseNavbar={handleCloseNavbar}
                                                isMobile
                                                branchId={branchId}
                                            />
                                        )}
                                    </Offcanvas.Header>
                                    <Offcanvas.Body>
                                        <WithRoleAccess permission={CompanyAccess.BranchCreate}>
                                            {hasAbilityToCreateBranch && (
                                                <div className="nav-item-menu rounded-1 bg-white py-0 mb-2">
                                                    <Link
                                                        className={`nav-link-menu text-primary ${isRegistrationOrCreateOwner ? "pe-none opacity-25" : ""}`}
                                                        to={`create-branch/${branchId}`}
                                                        onClick={handleCloseNavbar}
                                                    >
                                                        <i className="bi bi-plus-lg me-2"></i>
                                                        {translate("Add branch")}
                                                    </Link>
                                                </div>
                                            )}
                                        </WithRoleAccess>
                                        {!isRegistrationOrCreateOwner && (
                                            <SidebarBranchList
                                                branches={branches}
                                                handleCloseNavbar={handleCloseNavbar}
                                                handleChangeBranch={handleChangeBranch}
                                            />
                                        )}
                                        <SidebarMenu
                                            accordionsData={sidebarService.accordionContentService(
                                                String(branchId),
                                                String(mainCompany?.id),
                                                translate,
                                                isRegistrationOrCreateOwner
                                            )}
                                            handleCloseNavbar={handleCloseNavbar}
                                            isRegistrationOrCreateOwner={isRegistrationOrCreateOwner}
                                        />
                                        <PaymentExpired />
                                        {!isRegistrationOrCreateOwner && (
                                            <SidebarLoggedUser
                                                userData={userData}
                                                setShowCompaniesModal={setShowCompaniesModal}
                                                setShowChangePasswordModal={setShowChangePasswordModal}
                                            />
                                        )}
                                    </Offcanvas.Body>
                                </Offcanvas>
                            </Container>
                        </Navbar>
                    </div>
                    <div className="d-none d-md-block menu-container">
                        <SidebarHeader branchId={branchId} />
                        {!isRegistrationOrCreateOwner && (
                            <div className="d-flex justify-content-between mb-3 pe-3">
                                <SidebarSubheader company={mainCompany} />
                            </div>
                        )}
                        <WithRoleAccess permission={CompanyAccess.BranchCreate}>
                            {hasAbilityToCreateBranch && (
                                <div className="nav-item-menu rounded-1 bg-white py-1">
                                    <Link
                                        className={`nav-link-menu text-primary ${isRegistrationOrCreateOwner ? "pe-none opacity-25" : ""}`}
                                        to={`create-branch/${branchId}`}
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>
                                        {translate("Add branch")}
                                    </Link>
                                </div>
                            )}
                        </WithRoleAccess>
                        {!isRegistrationOrCreateOwner && (
                            <SidebarBranchList branches={branches} handleChangeBranch={handleChangeBranch} />
                        )}
                        <SidebarMenu
                            accordionsData={sidebarService.accordionContentService(
                                String(branchId),
                                String(mainCompany?.id),
                                translate,
                                isRegistrationOrCreateOwner
                            )}
                            isRegistrationOrCreateOwner={isRegistrationOrCreateOwner}
                        />
                        <PaymentExpired />
                        {!isRegistrationOrCreateOwner && (
                            <SidebarLoggedUser
                                userData={userData}
                                setShowCompaniesModal={setShowCompaniesModal}
                                setShowChangePasswordModal={setShowChangePasswordModal}
                            />
                        )}
                    </div>
                </Col>
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default React.memo(NavbarWrapper);
